// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, CircularProgress, FormControlLabel, IconButton, MenuItem, Select, Switch, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip as MuiTooltip, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Bar, BarChart, CartesianGrid, Cell, Label, LabelList, Legend, Pie, PieChart,
  PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, RadialBar, RadialBarChart,
  ResponsiveContainer, Scatter, ScatterChart, Tooltip, Treemap, XAxis, YAxis, ZAxis,
} from 'recharts';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { useAppDispatch, useAppSelector } from 'Hooks';
import {
  applicableWidgets, buildLayerMap, ChartSelector, computeSpec, computeWidget, dataTableForWidget, describeWidget, featuresForRange, featuresForSelector, focusedWidgets, WidgetData, WidgetSpec,
} from './dashboardMetrics';
import { CHART_COLORS_DARK } from 'Utils/chartColors';
import highlightAndFlash from './mapHighlight';
import { unpinWidget, setWidgetShared, unpinChart, setChartShared, setOrbitCenter, setChatHighlight, removeFeatureAnalysis, resetDashboardArtifacts, PinnedChart, HighlightTarget } from './dashboardSlice';
import { getChatbotOpen, getPinnedWidgets, getPinnedCharts, getFeatureAnalysis, getLoadedGdbId, getViewerLayers, getSelectedOrderId, getPopupAnalyses } from './dashboardSelectors';
import ArtifactErrorBoundary from 'Components/ArtifactErrorBoundary';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { setLayerVisible, setLayerShared, removeLayer, clearLayers, ViewerLayer } from 'Features/EsriSplitViewer/viewerLayersSlice';
import ChatChart from '../chatbot/ChatChart';
import { recomputeAggregateChart } from './chartViewRecompute';
import { LoadSharedPinsThunk, SaveSharedPinsThunk } from './dashboardThunk';
import sharedSetAfterRemoval from './sharedSet';
import stylesDef from './styles';

interface DashboardProps {
  view: MapView | SceneView | null;
  geodatabaseLayersRef: React.MutableRefObject<GroupLayer[]>;
  // Shared selection from the viewer; when set, the board focuses that feature's layer.
  selectedFeature: __esri.Graphic | null;
}

interface ComputedWidget {
  id: string;
  title: string;
  data: WidgetData | null;
  pinned: boolean;
}

// The dark-panel chart palette (shared with dashboardMetrics/WidgetView), used for bar/donut cells
// here. A superset of the old inline 6-color list — extra categories get distinct colors, not wraps.
const DONUT_COLORS = CHART_COLORS_DARK;

// ============================================================================================
// LEGACY — client-side AUTO-WIDGET subsystem. FROZEN, do not extend.
// ============================================================================================
// Khora is now the ONLY source of dashboard content: the user asks Khora, Khora reads the gdb and
// answers, and the user pins charts/widgets/analyses from those answers. Nothing is computed on the
// client from the raw gdb without a Khora answer + a user pin. The old "auto board" — which derived
// KPIs/charts from the loaded layers on load (Total features, Poles, Pole height, Clearance flags,
// "Interesting facts", the per-layer focus dropdown) — is kept behind this flag as a reference only.
//   false (current, Khora-driven ONLY): dashboard starts empty, shows only user-pinned Khora content;
//     the panel is hidden until something is pinned.
//   true (LEGACY auto board): the frozen client-computed widgets. Do NOT build new features on this
//     path — it exists so the old behavior can be restored, not extended. The curated widget functions
//     (g*/tele*/aec*/site*) + COMPUTE registry in dashboardMetrics.ts are the LEGACY block it drives.
const LEGACY_AUTO_WIDGETS = false;

// Bin raw values into ~8 buckets for a histogram rendered as a bar chart.
const toHistogram = (values: number[]): { label: string; count: number }[] => {
  if (!values.length) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const binCount = 8;
  const width = (max - min) / binCount || 1;
  const bins = Array.from({ length: binCount }, (_, index) => ({
    label: `${Math.round(min + index * width)}`,
    count: 0,
  }));
  values.forEach((value) => {
    const index = Math.min(binCount - 1, Math.floor((value - min) / width));
    bins[index].count += 1;
  });
  return bins;
};

// A chart is click-to-highlight when its datapoints carry selectors (bar/donut/scatter/bubble) or it's
// a histogram with a source layer/field (the clicked bin becomes a value range).
const isClickableChart = (data: WidgetData | null): boolean => {
  if (!data) return false;
  if (data.kind === 'hist') return Boolean(data.source);
  return 'selectors' in data && Boolean(data.selectors?.length);
};

// The center the cinematic Orbit should circle for a selection: the first feature's point, or the
// center of its extent. A plain point (Graphics aren't serializable through Redux).
const orbitCenterOf = (graphics: __esri.Graphic[]): { x: number; y: number; z?: number; wkid?: number } | null => {
  const geometry = graphics[0]?.geometry;
  if (!geometry) return null;
  const point = geometry.type === 'point' ? (geometry as __esri.Point) : geometry.extent?.center;
  if (!point) return null;
  return { x: point.x, y: point.y, z: point.z ?? undefined, wkid: point.spatialReference?.wkid };
};


const TREEMAP_COLORS = ['#1f6f9e', '#2e8b8b', '#3d8b53', '#8a8a2e', '#9e5a1f', '#7a3d8b', '#1f4f9e'];
const AXIS_TICK = { fill: '#bbb', fontSize: 10 };
const AXIS_LABEL = { fill: '#ddd', fontSize: 11 };
const AXIS_LABEL_VERT = { fill: '#ddd', fontSize: 11, textAnchor: 'middle' as const };
const TOOLTIP_CONTENT = {
  background: 'rgba(16, 31, 43, 0.96)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  borderRadius: 6,
  fontSize: 11,
  color: '#fff',
};
// recharts colors tooltip item text by the series color (e.g. a bubble's purple) — too dark on the
// near-black tooltip. Force white so every chart's tooltip text stays legible.
const TOOLTIP_ITEM = { color: '#fff' };
const nfmt = (value: number): string => value.toLocaleString('en-US');

interface TreemapTileProps {
  x: number; y: number; width: number; height: number; index: number; name: string; value: number;
}

// Custom treemap tile factory (passed to <Treemap content>); recharts calls the returned
// function with each node's geometry. Uses the layer's map color when supplied, labels name +
// count when the box is big enough.
const makeTreemapTile = (colors?: string[]) => (
  { x, y, width, height, index, name, value }: TreemapTileProps,
): React.ReactElement => (
  <g>
    <rect x={x} y={y} width={width} height={height} fill={colors?.[index] ?? TREEMAP_COLORS[index % TREEMAP_COLORS.length]} stroke="rgba(0, 0, 0, 0.5)" />
    {width > 52 && height > 26 && (
      <text x={x + 5} y={y + 15} fill="#fff" fontSize={10} fontWeight={600}>{name}</text>
    )}
    {width > 52 && height > 38 && (
      <text x={x + 5} y={y + 28} fill="rgba(255, 255, 255, 0.85)" fontSize={9}>{nfmt(value)}</text>
    )}
  </g>
);

const Dashboard: FC<DashboardProps> = ({ view, geodatabaseLayersRef, selectedFeature }) => {
  const theme = useTheme();
  // When Khora's chat drawer is open, the panel + launcher slide left so they aren't hidden under it.
  const chatbotOpen = useAppSelector(getChatbotOpen);
  const styles = stylesDef(theme, chatbotOpen);
  const dispatch = useAppDispatch();
  const pinnedSpecs = useAppSelector(getPinnedWidgets);
  const pinnedCharts = useAppSelector(getPinnedCharts);
  // Per-feature analyses the user pinned to the dashboard (onDashboard) — managed here (remove) and
  // shown in each feature's popup. [layerName, analysis] pairs for the label + remove.
  const featureAnalysis = useAppSelector(getFeatureAnalysis);
  const popupAnalyses = useAppSelector(getPopupAnalyses);
  const loadedGdbId = useAppSelector(getLoadedGdbId);
  // The order selected in the viewer — read (via a ref, so it doesn't retrigger the gdb effect) to tell a
  // same-order gdb RE-UPLOAD (clear the dashboard) apart from an ORDER SWITCH (the chat session-resume
  // clears + restores that order's own artifacts; clearing here would race and wipe the restore).
  const selectedOrderId = useAppSelector(getSelectedOrderId);
  const selectedOrderIdRef = useRef(selectedOrderId);
  selectedOrderIdRef.current = selectedOrderId;
  const dashboardGdbRef = useRef<{ order: string | null; gdb: string | null }>({ order: null, gdb: null });
  // Khora's spatial analysis layers (add_spatial_layer). They render as geometry in the 3D viewer; the
  // Dashboard surfaces them here too so all analysis (charts + images + layers) lives in one panel.
  const viewerLayers = useAppSelector(getViewerLayers);
  const pinnedChartsRef = useRef(pinnedCharts);
  pinnedChartsRef.current = pinnedCharts;
  const viewerLayersRef = useRef(viewerLayers);
  viewerLayersRef.current = viewerLayers;
  // Clicking a category on a pinned chart highlights the matching features on the map — the same
  // bridge the chat uses (setChatHighlight -> showChatHighlight in the viewer), so behavior matches.
  const handleChartHighlight = useCallback((
    label: string,
    geojson: GeoJSON.FeatureCollection,
    color?: string,
    target?: { layer: string; keyField: string; keyValues: string[] },
  ) => {
    // A chart click is transient: color it like the clicked bar/slice and auto-clear after 5s. A target
    // (layer + join keys) highlights the DISPLAYED features in place at real height; else draw the geojson.
    const targets: HighlightTarget[] | undefined = target
      ? [{ layer: target.layer, keyField: target.keyField, keyValues: target.keyValues, color }]
      : undefined;
    // action:'none': a dashboard chart click highlights features to READ them, it must not navigate the camera
    // (shouldFrameCamera) — framing no longer keys off ttlMs, so opt out explicitly.
    dispatch(setChatHighlight({ id: Date.now(), label, action: 'none', targets, geojson, color, ttlMs: 5000 }));
  }, [dispatch]);

  // Hover preview on a pinned chart (no 5s lock) + clear on chart-leave — mirrors the chat chart.
  const handleChartHover = useCallback((
    label: string,
    geojson: GeoJSON.FeatureCollection,
    color?: string,
    target?: { layer: string; keyField: string; keyValues: string[] },
  ) => {
    const targets: HighlightTarget[] | undefined = target
      ? [{ layer: target.layer, keyField: target.keyField, keyValues: target.keyValues, color }]
      : undefined;
    // action:'none': a hover PREVIEW must not navigate the camera (shouldFrameCamera) — the cursor is just on a bar.
    dispatch(setChatHighlight({ id: Date.now(), label, action: 'none', targets, geojson, color }));
  }, [dispatch]);
  const handleChartHoverClear = useCallback(() => { dispatch(setChatHighlight(null)); }, [dispatch]);

  // When the loaded gdb changes: first, if a NEW gdb loaded for the SAME order (a re-upload / version
  // change), CLEAR the dashboard — its pins/layers/analysis were computed on the previous gdb, so the
  // dashboard stays limited to its OWN gdb. (An order switch is excluded: prev.order differs, and the
  // chat session-resume already clears + restores that order's artifacts.) Then merge in the org-shared
  // pins for the loaded gdb (deduped by id; flagged shared). Best-effort — a failed fetch is ignored.
  useEffect(() => {
    if (!loadedGdbId) return undefined;
    const prev = dashboardGdbRef.current;
    if (prev.gdb && prev.gdb !== loadedGdbId && prev.order === selectedOrderIdRef.current) {
      dispatch(clearLayers());
      dispatch(resetDashboardArtifacts());
    }
    dashboardGdbRef.current = { order: selectedOrderIdRef.current, gdb: loadedGdbId };
    // The thunk fetches the org-shared set and merges it (own pins/layers/widgets flagged shared, a
    // teammate's added/pinned), guarding against a stale response if the gdb changes mid-fetch.
    dispatch(LoadSharedPinsThunk(loadedGdbId));
    return undefined;
  }, [loadedGdbId, dispatch]);

  // Share/unshare a pinned chart with the org for the loaded gdb. Optimistic; reverts if the save
  // fails. The shared set persists server-side per gdb, so org-mates see it on this order's dashboard.
  const handleToggleShare = useCallback(async (pinned: PinnedChart) => {
    if (!loadedGdbId) return;
    const willShare = pinned.sharedGdbId !== loadedGdbId;
    dispatch(setChartShared({ id: pinned.id, sharedGdbId: willShare ? loadedGdbId : undefined }));
    const shared = pinnedChartsRef.current
      .map((chart) => (chart.id === pinned.id ? { ...chart, sharedGdbId: willShare ? loadedGdbId : undefined } : chart))
      .filter((chart) => chart.sharedGdbId === loadedGdbId);
    const sharedLayers = viewerLayersRef.current.filter((layer) => layer.sharedGdbId === loadedGdbId);
    const sharedWidgets = pinnedSpecsRef.current.filter((spec) => spec.sharedGdbId === loadedGdbId);
    const ok = await dispatch(SaveSharedPinsThunk(loadedGdbId, shared, sharedLayers, sharedWidgets));
    if (!ok) dispatch(setChartShared({ id: pinned.id, sharedGdbId: willShare ? undefined : loadedGdbId }));
  }, [loadedGdbId, dispatch]);

  // Share/unshare a DASHBOARD LAYER with the org (same one-toggle mechanism as charts). Requires the
  // layer's S3 key (every Khora layer has one via dual delivery). Optimistic; reverts on failure.
  const handleToggleLayerShare = useCallback(async (layer: ViewerLayer) => {
    if (!loadedGdbId || !layer.key) return;
    const willShare = layer.sharedGdbId !== loadedGdbId;
    dispatch(setLayerShared({ id: layer.id, sharedGdbId: willShare ? loadedGdbId : undefined }));
    const sharedLayers = viewerLayersRef.current
      .map((entry) => (entry.id === layer.id ? { ...entry, sharedGdbId: willShare ? loadedGdbId : undefined } : entry))
      .filter((entry) => entry.sharedGdbId === loadedGdbId);
    const sharedCharts = pinnedChartsRef.current.filter((chart) => chart.sharedGdbId === loadedGdbId);
    const sharedWidgets = pinnedSpecsRef.current.filter((spec) => spec.sharedGdbId === loadedGdbId);
    const ok = await dispatch(SaveSharedPinsThunk(loadedGdbId, sharedCharts, sharedLayers, sharedWidgets));
    if (!ok) dispatch(setLayerShared({ id: layer.id, sharedGdbId: willShare ? undefined : loadedGdbId }));
  }, [loadedGdbId, dispatch]);

  // Share/unshare a pinned WIDGET with the org — same one-toggle mechanism. Widget specs are
  // recomputable metadata, so recipients' dashboards compute them against their own loaded gdb.
  const handleToggleWidgetShare = useCallback(async (spec: WidgetSpec) => {
    if (!loadedGdbId) return;
    const willShare = spec.sharedGdbId !== loadedGdbId;
    dispatch(setWidgetShared({ id: spec.id, sharedGdbId: willShare ? loadedGdbId : undefined }));
    const sharedWidgets = pinnedSpecsRef.current
      .map((entry) => (entry.id === spec.id ? { ...entry, sharedGdbId: willShare ? loadedGdbId : undefined } : entry))
      .filter((entry) => entry.sharedGdbId === loadedGdbId);
    const sharedCharts = pinnedChartsRef.current.filter((chart) => chart.sharedGdbId === loadedGdbId);
    const sharedLayers = viewerLayersRef.current.filter((layer) => layer.sharedGdbId === loadedGdbId);
    const ok = await dispatch(SaveSharedPinsThunk(loadedGdbId, sharedCharts, sharedLayers, sharedWidgets));
    if (!ok) dispatch(setWidgetShared({ id: spec.id, sharedGdbId: willShare ? undefined : loadedGdbId }));
  }, [loadedGdbId, dispatch]);

  // Removing a pin that was org-SHARED must also drop it from the server's shared set — otherwise
  // loadSharedPins re-adds it (for everyone) on the next gdb load. Recompute the shared set here (the
  // refs haven't re-rendered yet) minus the removed id. (The session-persistence side, in the chatbot,
  // separately keeps a removal from coming back via the saved session.)
  const persistSharedRemoval = useCallback((removed: { chartId?: string; widgetId?: string; layerId?: string }) => {
    if (!loadedGdbId) return;
    const charts = sharedSetAfterRemoval(pinnedChartsRef.current, loadedGdbId, removed.chartId);
    const layers = sharedSetAfterRemoval(viewerLayersRef.current, loadedGdbId, removed.layerId);
    const widgets = sharedSetAfterRemoval(pinnedSpecsRef.current, loadedGdbId, removed.widgetId);
    // Best-effort; the local removal is already applied. The thunk swallows failure (returns false).
    dispatch(SaveSharedPinsThunk(loadedGdbId, charts, layers, widgets));
  }, [loadedGdbId, dispatch]);
  const handleUnpinChart = useCallback((pinned: PinnedChart) => {
    dispatch(unpinChart(pinned.id));
    if (pinned.sharedGdbId === loadedGdbId) persistSharedRemoval({ chartId: pinned.id });
  }, [dispatch, loadedGdbId, persistSharedRemoval]);
  const handleUnpinWidget = useCallback((widgetId: string) => {
    const wasShared = pinnedSpecsRef.current.find((s) => s.id === widgetId)?.sharedGdbId === loadedGdbId;
    dispatch(unpinWidget(widgetId));
    if (wasShared) persistSharedRemoval({ widgetId });
  }, [dispatch, loadedGdbId, persistSharedRemoval]);
  const handleRemoveLayer = useCallback((layer: ViewerLayer) => {
    dispatch(removeLayer(layer.id));
    if (layer.sharedGdbId === loadedGdbId) persistSharedRemoval({ layerId: layer.id });
  }, [dispatch, loadedGdbId, persistSharedRemoval]);

  const [viewOnly, setViewOnly] = useState(false);
  // Default CLOSED: the dashboard shows only its launcher icon until the user clicks to open it.
  const [collapsed, setCollapsed] = useState(true);
  const [widgets, setWidgets] = useState<ComputedWidget[]>([]);
  const [loading, setLoading] = useState(false);
  const [focusedLayer, setFocusedLayer] = useState<string | null>(null);
  const [layerNames, setLayerNames] = useState<string[]>([]);
  // Per-card transparency panels: the widget id whose data table / methodology is expanded (one each).
  const [openTable, setOpenTable] = useState<string | null>(null);
  const [openInfo, setOpenInfo] = useState<string | null>(null);
  // View-scoped recomputed chart rows (pinId → data) for recomputable count charts in "current view only".
  // Empty when the toggle is off → charts render their baked full-extent data.
  const [chartViewData, setChartViewData] = useState<Record<string, PinnedChart['chart']['data']>>({});
  const viewOnlyRef = useRef(viewOnly);
  viewOnlyRef.current = viewOnly;
  const pinnedSpecsRef = useRef(pinnedSpecs);
  pinnedSpecsRef.current = pinnedSpecs;
  const focusedLayerRef = useRef(focusedLayer);
  focusedLayerRef.current = focusedLayer;
  // Monotonic token so a slow older chart recompute (an earlier pan still resolving) can't overwrite a newer
  // extent's result — otherwise chartViewData could hold stale numbers under the green "Current view" badge.
  const recomputeGenRef = useRef(0);

  const recompute = useCallback(async () => {
    if (!view) return;
    const generation = (recomputeGenRef.current += 1);
    const layers = buildLayerMap(geodatabaseLayersRef.current || []);
    setLayerNames(Array.from(layers.keys()).sort());
    // GeoJSON layers may still be loading on first compute — until they finish, layer.fields is empty
    // (so renamed-column lookups miss) and queries can come back empty. Wait for all layers to load so
    // widgets reflect real data instead of a mid-load snapshot (this was emptying pole/clearance charts).
    await Promise.all(Array.from(layers.values()).map((layer) => layer.load().catch(() => {})));
    const ctx = { layers, view, viewOnly: viewOnlyRef.current };

    // View-scoped chart recompute: re-tally each recomputable pinned count chart against the current extent.
    // Runs BEFORE the widget branches (which early-return) so charts rescope regardless of the widget board.
    const nextChartData: Record<string, PinnedChart['chart']['data']> = {};
    if (viewOnlyRef.current) {
      await Promise.all(pinnedChartsRef.current.map(async (pinned) => {
        if (!pinned.chart.recompute) return;
        const rescoped = await recomputeAggregateChart(pinned.chart, ctx);
        if (rescoped) nextChartData[pinned.id] = rescoped;
      }));
    }
    // Skip if a newer recompute began while our per-chart queries were in flight (it will set the fresh data).
    if (generation === recomputeGenRef.current) setChartViewData(nextChartData);

    // Single-layer focus (dropdown or a map click) overrides the curated board.
    const focus = focusedLayerRef.current;
    if (LEGACY_AUTO_WIDGETS && focus && layers.has(focus)) {
      setLoading(true);
      const focused = await focusedWidgets(ctx, focus);
      if (generation !== recomputeGenRef.current) return; // a newer recompute owns the board
      setWidgets(focused.map((widget) => ({ ...widget, pinned: false })));
      setLoading(false);
      return;
    }

    const defs = LEGACY_AUTO_WIDGETS ? applicableWidgets(layers) : [];
    const specs = pinnedSpecsRef.current;
    if (!defs.length && !specs.length) {
      // Current generation owns the board AND the spinner: clear a `loading` an earlier recompute left on
      // (an older run set it true then bailed on its stale-guard), else the spinner sticks on this path.
      if (generation === recomputeGenRef.current) { setWidgets([]); setLoading(false); }
      return;
    }
    setLoading(true);
    const [registry, pinned] = await Promise.all([
      Promise.all(defs.map(async (def) => ({ id: def.id, title: def.title, pinned: false, data: await computeWidget(def.id, ctx) }))),
      // computeSpec renders each pinned widget; for scalar KPIs at whole-gdb scope it prefers the
      // Lambda's snapshot (Khora's gdb value) over re-deriving from the published viewer layer.
      Promise.all(specs.map(async (spec) => ({ id: spec.id, title: spec.title, pinned: true, data: await computeSpec(spec, ctx) }))),
    ]);
    if (generation !== recomputeGenRef.current) return; // a newer extent's recompute already owns the board
    // Pinned widgets first, so what the user just asked Khora for is at the top.
    setWidgets([...pinned, ...registry]);
    setLoading(false);
  }, [view, geodatabaseLayersRef]);

  // Recompute on mount, when "current view only" changes, when Khora pins/unpins a widget, and
  // when the focused layer changes (dropdown or map click).
  useEffect(() => {
    recompute();
  }, [recompute, viewOnly, pinnedSpecs, pinnedCharts, focusedLayer]);

  // In view-only mode, recompute once the map settles after a pan/zoom.
  useEffect(() => {
    if (!view || !viewOnly) return undefined;
    const handle = reactiveUtils.when(() => view.stationary, () => { recompute(); });
    return () => handle.remove();
  }, [view, viewOnly, recompute]);

  // The shared selection focuses the clicked feature's layer in the dashboard.
  useEffect(() => {
    const title = selectedFeature?.layer?.title;
    if (title) setFocusedLayer(title);
  }, [selectedFeature]);

  // Click a chart datapoint -> highlight the features it represents on the map and fly to them.
  const handleSelector = useCallback(async (selector?: ChartSelector | null) => {
    if (!view || !selector) return;
    const ctx = { layers: buildLayerMap(geodatabaseLayersRef.current || []), view, viewOnly: viewOnlyRef.current };
    const groups = await featuresForSelector(ctx, selector);
    const graphics = groups.flatMap((group) => group.features);
    dispatch(setOrbitCenter(orbitCenterOf(graphics)));
    await highlightAndFlash(view, groups);
  }, [view, geodatabaseLayersRef, dispatch]);

  // Click a histogram bin -> highlight features whose value falls in that bin's range (the bins match
  // toHistogram: 8 equal-width bins; the last one is inclusive of the max).
  const handleHistBin = useCallback(async (values: number[], source: { layer: string; field: string }, binIndex: number) => {
    if (!view || !values.length || binIndex < 0) return;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const binCount = 8;
    const width = (max - min) / binCount || 1;
    const ctx = { layers: buildLayerMap(geodatabaseLayersRef.current || []), view, viewOnly: viewOnlyRef.current };
    const groups = await featuresForRange(ctx, source.layer, source.field, min + binIndex * width, min + (binIndex + 1) * width, binIndex === binCount - 1);
    const graphics = groups.flatMap((group) => group.features);
    dispatch(setOrbitCenter(orbitCenterOf(graphics)));
    await highlightAndFlash(view, groups);
  }, [view, geodatabaseLayersRef, dispatch]);

  if (!view) return null;

  const kpis = widgets.filter((widget) => widget.data?.kind === 'kpi');
  const charts = widgets.filter((widget) => widget.data && widget.data.kind !== 'kpi');
  // A pinned widget the user explicitly asked Khora for must not silently vanish when it can't be
  // computed (no matching data / error) — show a placeholder card instead so it's clear why.
  const emptyPinned = widgets.filter((widget) => widget.pinned && !widget.data);
  const hasContent = kpis.length > 0 || charts.length > 0 || emptyPinned.length > 0 || pinnedCharts.length > 0 || viewerLayers.length > 0 || popupAnalyses.length > 0;

  // Khora-driven mode: with nothing pinned, render nothing at all (no panel chrome) so the viewer
  // stays clean. When LEGACY_AUTO_WIDGETS is true we fall through to the panel's own "no layers" state.
  if (!LEGACY_AUTO_WIDGETS && !hasContent) return null;

  if (collapsed) {
    return (
      <MuiTooltip title="Show dashboard" placement="left">
        <IconButton sx={styles.launcher} onClick={() => setCollapsed(false)} aria-label="Show dashboard">
          <BarChartIcon />
        </IconButton>
      </MuiTooltip>
    );
  }

  const renderChart = (widget: ComputedWidget) => {
    const { data } = widget;
    if (!data) return null;
    // Datapoint clicks -> highlight the features behind them on the map. `selectors` align 1:1 with
    // the datapoints (bar/donut/scatter/bubble); histograms carry a `source` + derive the bin range.
    const selectors = 'selectors' in data ? data.selectors : undefined;
    const onPoint = selectors ? (_entry: unknown, index: number) => handleSelector(selectors[index]) : undefined;
    const histInfo = data.kind === 'hist' && data.source ? { values: data.values, source: data.source } : null;
    const onHist = histInfo
      ? (state: { activeTooltipIndex?: number | null }) => {
        if (state?.activeTooltipIndex != null) handleHistBin(histInfo.values, histInfo.source, state.activeTooltipIndex);
      }
      : undefined;
    if (data.kind === 'bar') {
      const rows = data.x.map((label, index) => ({ label, value: data.y[index] }));
      if (data.horizontal) {
        return (
          <ResponsiveContainer width="100%" height={Math.max(150, rows.length * 26 + 34)}>
            <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 30, bottom: 22, left: 4 }}>
              <CartesianGrid horizontal={false} stroke="rgba(255, 255, 255, 0.08)" />
              <XAxis type="number" tick={AXIS_TICK} tickFormatter={nfmt}>
                <Label value={data.yTitle} position="insideBottom" offset={-8} style={AXIS_LABEL} />
              </XAxis>
              <YAxis type="category" dataKey="label" tick={AXIS_TICK} width={104} />
              <Tooltip cursor={false} contentStyle={TOOLTIP_CONTENT} itemStyle={TOOLTIP_ITEM} formatter={(value: number) => [nfmt(value), data.yTitle]} />
              <Bar dataKey="value" radius={[0, 3, 3, 0]} onClick={onPoint} activeBar={{ stroke: '#fff', strokeWidth: 1.5 }}>
                {rows.map((row, index) => <Cell key={row.label} fill={data.colors?.[index] ?? DONUT_COLORS[index % DONUT_COLORS.length]} />)}
                <LabelList dataKey="value" position="right" formatter={nfmt} style={{ fill: '#eee', fontSize: 9 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
      }
      return (
        <ResponsiveContainer width="100%" height={188}>
          <BarChart data={rows} margin={{ top: 12, right: 8, bottom: 34, left: 6 }}>
            <CartesianGrid vertical={false} stroke="rgba(255, 255, 255, 0.08)" />
            <XAxis dataKey="label" tick={AXIS_TICK} interval={0} angle={-20} textAnchor="end" height={50}>
              <Label value={data.xTitle} position="insideBottom" offset={-4} style={AXIS_LABEL} />
            </XAxis>
            <YAxis tick={AXIS_TICK} width={46} tickFormatter={nfmt}>
              <Label value={data.yTitle} angle={-90} position="insideLeft" style={AXIS_LABEL_VERT} />
            </YAxis>
            <Tooltip cursor={false} contentStyle={TOOLTIP_CONTENT} itemStyle={TOOLTIP_ITEM} formatter={(value: number) => [nfmt(value), data.yTitle]} />
            <Bar dataKey="value" radius={[3, 3, 0, 0]} onClick={onPoint} activeBar={{ stroke: '#fff', strokeWidth: 1.5 }}>
              {rows.map((row, index) => <Cell key={row.label} fill={data.colors?.[index] ?? DONUT_COLORS[index % DONUT_COLORS.length]} />)}
              <LabelList dataKey="value" position="top" formatter={nfmt} style={{ fill: '#ddd', fontSize: 9 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }
    if (data.kind === 'hist') {
      const rows = toHistogram(data.values);
      const gradientId = `histFill-${widget.id}`;
      // Binned frequency data is discrete — render as contiguous bars (a true histogram), not a
      // smooth area, which would imply a continuous trend between bins.
      return (
        <ResponsiveContainer width="100%" height={184}>
          <BarChart data={rows} margin={{ top: 12, right: 10, bottom: 32, left: 6 }} barCategoryGap="2%" onClick={onHist}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#01A1DD" stopOpacity={0.95} />
                <stop offset="100%" stopColor="#01A1DD" stopOpacity={0.4} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="rgba(255, 255, 255, 0.08)" />
            <XAxis dataKey="label" tick={AXIS_TICK} interval={0} height={36}>
              <Label value={data.xTitle} position="insideBottom" offset={-4} style={AXIS_LABEL} />
            </XAxis>
            <YAxis tick={AXIS_TICK} width={40} allowDecimals={false}>
              <Label value="Count" angle={-90} position="insideLeft" style={AXIS_LABEL_VERT} />
            </YAxis>
            <Tooltip cursor={{ fill: 'rgba(255, 255, 255, 0.06)' }} contentStyle={TOOLTIP_CONTENT} itemStyle={TOOLTIP_ITEM} formatter={(value: number) => [nfmt(value), 'Count']} labelFormatter={(label) => `${data.xTitle}: ${label}`} />
            <Bar dataKey="count" fill={`url(#${gradientId})`} stroke="#01A1DD" strokeWidth={0.5} radius={[2, 2, 0, 0]} activeBar={{ stroke: '#fff', strokeWidth: 1.5 }} />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    if (data.kind === 'donut') {
      const total = data.values.reduce((sum, value) => sum + value, 0);
      const rows = data.labels.map((label, index) => ({ name: label, value: data.values[index] }));
      return (
        <ResponsiveContainer width="100%" height={196}>
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="name" innerRadius={44} outerRadius={66} paddingAngle={2} onClick={onPoint}>
              {rows.map((row, index) => <Cell key={row.name} fill={data.colors?.[index] ?? DONUT_COLORS[index % DONUT_COLORS.length]} />)}
              <Label value={nfmt(total)} position="center" style={{ fill: '#fff', fontSize: 15, fontWeight: 700 }} />
            </Pie>
            <Legend verticalAlign="bottom" height={28} iconSize={8} wrapperStyle={{ fontSize: 10, color: '#ccc' }} />
            <Tooltip contentStyle={TOOLTIP_CONTENT} itemStyle={TOOLTIP_ITEM} formatter={(value: number, name: string) => [`${nfmt(value)} (${total ? Math.round((value / total) * 100) : 0}%)`, name]} />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    if (data.kind === 'treemap') {
      return (
        <ResponsiveContainer width="100%" height={200}>
          <Treemap data={data.nodes} dataKey="value" nameKey="name" content={makeTreemapTile(data.colors)} isAnimationActive={false}>
            <Tooltip contentStyle={TOOLTIP_CONTENT} itemStyle={TOOLTIP_ITEM} formatter={(value: number) => [nfmt(value), data.valueTitle]} />
          </Treemap>
        </ResponsiveContainer>
      );
    }
    if (data.kind === 'radial') {
      const rows = [{ name: data.valueLabel, value: data.value }];
      return (
        <Box>
          <Box sx={styles.gaugeWrap}>
            <ResponsiveContainer width="100%" height={148}>
              <RadialBarChart data={rows} startAngle={210} endAngle={-30} innerRadius="62%" outerRadius="100%">
                <PolarAngleAxis type="number" domain={[0, data.max]} angleAxisId={0} tick={false} />
                <RadialBar dataKey="value" angleAxisId={0} background={{ fill: 'rgba(255, 255, 255, 0.12)' }} cornerRadius={6} fill="#01A1DD" />
              </RadialBarChart>
            </ResponsiveContainer>
            <Typography sx={styles.gaugeValue}>{data.valueLabel}</Typography>
          </Box>
          <Typography variant="caption" sx={styles.kpiHelp}>{data.help}</Typography>
        </Box>
      );
    }
    if (data.kind === 'radar') {
      const rows = data.axes.map((axis, index) => ({ axis, value: data.values[index] }));
      return (
        <ResponsiveContainer width="100%" height={224}>
          <RadarChart data={rows} margin={{ top: 8, right: 22, bottom: 8, left: 22 }}>
            <PolarGrid stroke="rgba(255, 255, 255, 0.14)" />
            <PolarAngleAxis dataKey="axis" tick={AXIS_TICK} />
            <PolarRadiusAxis angle={90} tick={AXIS_TICK} tickFormatter={nfmt} />
            <Radar dataKey="value" stroke="#01A1DD" strokeWidth={2} fill="#01A1DD" fillOpacity={0.45} />
            <Tooltip contentStyle={TOOLTIP_CONTENT} itemStyle={TOOLTIP_ITEM} formatter={(value: number) => [nfmt(value), data.valueTitle]} />
          </RadarChart>
        </ResponsiveContainer>
      );
    }
    if (data.kind === 'scatter') {
      return (
        <ResponsiveContainer width="100%" height={210}>
          <ScatterChart margin={{ top: 12, right: 14, bottom: 34, left: 6 }}>
            <CartesianGrid stroke="rgba(255, 255, 255, 0.08)" />
            <XAxis type="number" dataKey="x" tick={AXIS_TICK} tickFormatter={nfmt}>
              <Label value={data.xTitle} position="insideBottom" offset={-4} style={AXIS_LABEL} />
            </XAxis>
            <YAxis type="number" dataKey="y" tick={AXIS_TICK} width={46} tickFormatter={nfmt}>
              <Label value={data.yTitle} angle={-90} position="insideLeft" style={AXIS_LABEL_VERT} />
            </YAxis>
            <ZAxis range={[36, 36]} />
            <Tooltip contentStyle={TOOLTIP_CONTENT} itemStyle={TOOLTIP_ITEM} cursor={{ strokeDasharray: '3 3' }} formatter={(value: number, name: string) => [nfmt(value), name === 'x' ? data.xTitle : data.yTitle]} />
            <Scatter data={data.points} fill="#01A1DD" fillOpacity={0.75} onClick={onPoint} />
          </ScatterChart>
        </ResponsiveContainer>
      );
    }
    if (data.kind === 'bubble') {
      const sizes = data.points.map((point) => point.z);
      const sizeDomain: [number, number] = [Math.min(...sizes), Math.max(...sizes)];
      return (
        <ResponsiveContainer width="100%" height={220}>
          <ScatterChart margin={{ top: 12, right: 16, bottom: 34, left: 6 }}>
            <CartesianGrid stroke="rgba(255, 255, 255, 0.08)" />
            <XAxis type="number" dataKey="x" name={data.xTitle} tick={AXIS_TICK} tickFormatter={nfmt}>
              <Label value={data.xTitle} position="insideBottom" offset={-4} style={AXIS_LABEL} />
            </XAxis>
            <YAxis type="number" dataKey="y" name={data.yTitle} tick={AXIS_TICK} width={46} tickFormatter={nfmt}>
              <Label value={data.yTitle} angle={-90} position="insideLeft" style={AXIS_LABEL_VERT} />
            </YAxis>
            <ZAxis type="number" dataKey="z" name={data.zTitle} range={[40, 420]} domain={sizeDomain} />
            <Tooltip contentStyle={TOOLTIP_CONTENT} itemStyle={TOOLTIP_ITEM} cursor={{ strokeDasharray: '3 3' }} formatter={(value: number, name: string) => [nfmt(value), name]} />
            <Scatter data={data.points} fill="#B9E2F1" fillOpacity={0.6} onClick={onPoint} />
          </ScatterChart>
        </ResponsiveContainer>
      );
    }
    if (data.kind === 'waterfall') {
      // Floating bars: a "total" bar is anchored at 0; a delta bar floats between the running
      // balance before and after the step. recharts draws a [min, max] value as a floating bar.
      let running = 0;
      const rows = data.steps.map((step) => {
        if (step.isTotal) {
          running = step.value;
          return { label: step.label, range: [0, step.value] as [number, number], delta: step.value, fill: '#01A1DD' };
        }
        const start = running;
        running += step.value;
        const lo = Math.min(start, running);
        const hi = Math.max(start, running);
        return { label: step.label, range: [lo, hi] as [number, number], delta: step.value, fill: step.value < 0 ? '#FF9424' : '#01A1DD' };
      });
      return (
        <ResponsiveContainer width="100%" height={210}>
          <BarChart data={rows} margin={{ top: 12, right: 8, bottom: 42, left: 6 }}>
            <CartesianGrid vertical={false} stroke="rgba(255, 255, 255, 0.08)" />
            <XAxis dataKey="label" tick={AXIS_TICK} interval={0} angle={-20} textAnchor="end" height={54} />
            <YAxis tick={AXIS_TICK} width={52} tickFormatter={nfmt}>
              <Label value={data.valueTitle} angle={-90} position="insideLeft" style={AXIS_LABEL_VERT} />
            </YAxis>
            <Tooltip contentStyle={TOOLTIP_CONTENT} itemStyle={TOOLTIP_ITEM} formatter={(value: number | [number, number]) => [Array.isArray(value) ? nfmt(Math.abs(value[1] - value[0])) : nfmt(value), data.valueTitle]} />
            <Bar dataKey="range" radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {rows.map((row) => <Cell key={row.label} fill={row.fill} />)}
              <LabelList dataKey="delta" position="top" formatter={nfmt} style={{ fill: '#ddd', fontSize: 9 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }
    if (data.kind === 'stacked') {
      const rows = data.categories.map((category, index) => {
        const row: Record<string, string | number> = { category };
        data.series.forEach((series) => { row[series.name] = series.values[index]; });
        return row;
      });
      return (
        <ResponsiveContainer width="100%" height={216}>
          <BarChart data={rows} stackOffset="expand" margin={{ top: 8, right: 8, bottom: 42, left: 6 }}>
            <CartesianGrid vertical={false} stroke="rgba(255, 255, 255, 0.08)" />
            <XAxis dataKey="category" tick={AXIS_TICK} interval={0} angle={-20} textAnchor="end" height={54} />
            <YAxis tick={AXIS_TICK} width={40} tickFormatter={(value: number) => `${Math.round(value * 100)}%`} />
            <Tooltip contentStyle={TOOLTIP_CONTENT} itemStyle={TOOLTIP_ITEM} formatter={(value: number) => nfmt(value)} />
            <Legend verticalAlign="top" height={24} iconSize={8} wrapperStyle={{ fontSize: 10, color: '#ccc' }} />
            {data.series.map((series) => (
              <Bar key={series.name} dataKey={series.name} stackId="mix" fill={series.color} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }
    if (data.kind === 'facts') {
      return (
        <Box sx={styles.factsList}>
          {data.items.map((item) => (
            <Box key={item.label} sx={styles.factRow}>
              <Typography sx={styles.factLabel}>{item.label}</Typography>
              <Typography sx={styles.factValue}>{item.value}</Typography>
            </Box>
          ))}
        </Box>
      );
    }
    if (data.kind === 'table') {
      const columns = data.rows.length ? Object.keys(data.rows[0]) : [];
      return (
        <Box sx={styles.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => <TableCell key={column} sx={styles.thCell}>{column}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((row) => (
                <TableRow key={columns.map((column) => row[column]).join('-')}>
                  {columns.map((column) => <TableCell key={column} sx={styles.tdCell}>{row[column]}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      );
    }
    if (data.kind === 'heatmap') {
      const maxCount = Math.max(1, ...data.matrix.flat());
      return (
        <Box sx={styles.heatmapWrap}>
          <Box sx={styles.heatmapGrid} style={{ gridTemplateColumns: `minmax(52px, auto) repeat(${data.cols.length}, 1fr)` }}>
            <Box />
            {data.cols.map((col) => <Typography key={col} sx={styles.heatmapColLabel}>{col}</Typography>)}
            {data.rows.map((rowLabel, ri) => (
              <React.Fragment key={rowLabel}>
                <Typography sx={styles.heatmapRowLabel} title={rowLabel}>{rowLabel}</Typography>
                {data.cols.map((col, ci) => {
                  const count = data.matrix[ri][ci];
                  const intensity = count / maxCount;
                  return (
                    <Box
                      key={col}
                      sx={styles.heatmapCell}
                      title={`${rowLabel} · ${data.colTitle} ${col}: ${count} ${data.valueTitle}`}
                      style={{
                        backgroundColor: `rgba(1, 161, 221, ${(0.06 + intensity * 0.9).toFixed(3)})`,
                        color: intensity > 0.5 ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      {count > 0 ? count : ''}
                    </Box>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
          <Typography sx={styles.heatmapAxis}>{`${data.colTitle} →   ·   color = ${data.valueTitle}`}</Typography>
        </Box>
      );
    }
    return null;
  };

  // The data rows behind a chart, shown when its table button is toggled on.
  const renderDataTable = (data: WidgetData | null) => {
    const table = dataTableForWidget(data);
    if (!table) return <Typography variant="caption" sx={styles.empty}>No table for this chart.</Typography>;
    return (
      <Box sx={styles.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {table.columns.map((column) => <TableCell key={column} sx={styles.thCell}>{column}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.map((row) => (
              <TableRow key={row.join('|')}>
                {row.map((cell, index) => <TableCell key={table.columns[index]} sx={styles.tdCell}>{cell}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Box sx={styles.panel}>
      <Box sx={styles.head}>
        <Box sx={styles.header}>
          <Typography sx={styles.title}>Dashboard</Typography>
          <IconButton size="small" sx={{ color: theme.palette.common.white }} onClick={() => setCollapsed(true)} aria-label="Hide dashboard">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* "Current view only" scopes the recomputable widgets to the map extent. Shown whenever the
            dashboard has content — NOT gated to LEGACY_AUTO_WIDGETS, which used to hide it entirely. */}
        {(LEGACY_AUTO_WIDGETS || hasContent) && (
          <>
            <FormControlLabel
              sx={styles.toggleRow}
              control={<Switch size="small" checked={viewOnly} onChange={(event) => setViewOnly(event.target.checked)} />}
              label={<Typography variant="caption" sx={{ color: 'common.white' }}>Current view only</Typography>}
              labelPlacement="start"
            />
            {viewOnly && (
              <Typography variant="caption" sx={styles.kpiHelp}>
                Widgets are restricted to the current map view; features crossing the edge are clipped to it.
                Pinned charts show their full-extent snapshot (badged below).
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box sx={styles.body}>

      {LEGACY_AUTO_WIDGETS && layerNames.length > 0 && (
        <Select
          value={focusedLayer ?? ''}
          onChange={(event) => setFocusedLayer(event.target.value ? String(event.target.value) : null)}
          displayEmpty
          size="small"
          sx={styles.layerSelect}
          MenuProps={{ PaperProps: { sx: styles.layerMenu } }}
        >
          <MenuItem value="">All layers</MenuItem>
          {layerNames.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
        </Select>
      )}
      {LEGACY_AUTO_WIDGETS && focusedLayer && (
        <Typography variant="caption" sx={styles.kpiHelp}>
          {`Focused on ${focusedLayer} — click a feature to switch, or choose All layers.`}
        </Typography>
      )}

      {loading && <CircularProgress size={18} sx={{ alignSelf: 'center', color: theme.palette.common.white }} />}

      {!loading && !hasContent && (
        <Typography variant="body2" sx={styles.empty}>No geodatabase layers loaded yet.</Typography>
      )}


      {kpis.length > 0 && (
        <Box sx={styles.kpiGrid}>
          {kpis.map((widget) => {
            if (widget.data?.kind !== 'kpi') return null;
            const spec = pinnedSpecs.find((entry) => entry.id === widget.id);
            const derivation = spec?.derivation;
            return (
              <Box key={widget.id} sx={styles.card}>
                <Typography sx={styles.kpiValue}>{widget.data.value}</Typography>
                <Box sx={styles.cardFooter}>
                  <Typography sx={styles.kpiHelp}>{widget.title}</Typography>
                  <Box sx={styles.cardTools}>
                    {derivation && (
                      <MuiTooltip title={derivation}>
                        <IconButton size="small" sx={styles.cardToolBtn} aria-label={`How ${widget.title} is computed`}>
                          <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </MuiTooltip>
                    )}
                    {widget.pinned && spec && (
                      <MuiTooltip title={spec.sharedGdbId === loadedGdbId ? 'Unshare with org' : 'Share with org'}>
                        <IconButton size="small" sx={styles.cardToolBtn} onClick={() => handleToggleWidgetShare(spec)} aria-label={spec.sharedGdbId === loadedGdbId ? `Unshare ${widget.title}` : `Share ${widget.title} with org`}>
                          <ShareOutlinedIcon sx={{ fontSize: 13, color: spec.sharedGdbId === loadedGdbId ? 'primary.main' : undefined }} />
                        </IconButton>
                      </MuiTooltip>
                    )}
                    {widget.pinned && (
                      <IconButton size="small" sx={styles.unpin} onClick={() => handleUnpinWidget(widget.id)} aria-label={`Unpin ${widget.title}`}>
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {charts.map((widget) => (
        <Box key={widget.id} sx={styles.chartCard}>
          <Box sx={styles.cardFooter}>
            <Typography sx={styles.chartTitle}>{widget.title}</Typography>
            <Box sx={styles.cardTools}>
              <MuiTooltip title="Show the data table behind this chart">
                <IconButton
                  size="small"
                  sx={styles.cardToolBtn}
                  aria-label={`Data table for ${widget.title}`}
                  onClick={() => setOpenTable((current) => (current === widget.id ? null : widget.id))}
                >
                  <TableChartOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </MuiTooltip>
              <MuiTooltip title="How this chart is derived from the data">
                <IconButton
                  size="small"
                  sx={styles.cardToolBtn}
                  aria-label={`How ${widget.title} is derived`}
                  onClick={() => setOpenInfo((current) => (current === widget.id ? null : widget.id))}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </MuiTooltip>
              {widget.pinned && (() => {
                const spec = pinnedSpecs.find((entry) => entry.id === widget.id);
                if (!spec) return null;
                return (
                  <MuiTooltip title={spec.sharedGdbId === loadedGdbId ? 'Unshare with org' : 'Share with org'}>
                    <IconButton size="small" sx={styles.cardToolBtn} onClick={() => handleToggleWidgetShare(spec)} aria-label={spec.sharedGdbId === loadedGdbId ? `Unshare ${widget.title}` : `Share ${widget.title} with org`}>
                      <ShareOutlinedIcon sx={{ fontSize: 14, color: spec.sharedGdbId === loadedGdbId ? 'primary.main' : undefined }} />
                    </IconButton>
                  </MuiTooltip>
                );
              })()}
              {widget.pinned && (
                <IconButton size="small" sx={styles.unpin} onClick={() => handleUnpinWidget(widget.id)} aria-label={`Unpin ${widget.title}`}>
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              )}
            </Box>
          </Box>
          <Box style={isClickableChart(widget.data) ? { cursor: 'pointer' } : undefined}>
            {renderChart(widget)}
          </Box>
          {openInfo === widget.id && (
            <Box sx={styles.methodPanel}>
              {describeWidget(widget.data, widget.id).map((line) => (
                <Typography key={line} sx={styles.methodLine}>{line}</Typography>
              ))}
            </Box>
          )}
          {openTable === widget.id && renderDataTable(widget.data)}
        </Box>
      ))}

      {emptyPinned.map((widget) => (
        <Box key={widget.id} sx={styles.chartCard}>
          <Box sx={styles.cardFooter}>
            <Typography sx={styles.chartTitle}>{widget.title}</Typography>
            <IconButton size="small" sx={styles.unpin} onClick={() => handleUnpinWidget(widget.id)} aria-label={`Unpin ${widget.title}`}>
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
          <Typography variant="caption" sx={styles.empty}>No data to show — this layer/field returned nothing for the current view.</Typography>
        </Box>
      ))}

      {pinnedCharts.map((pinned) => {
        // In "current view only", a count chart carrying a `recompute` descriptor is re-tallied to the map
        // extent (chartViewData[id]); the badge tracks the ACTUAL state, so a chart that can't rescope — or
        // whose recompute hasn't produced yet — honestly reads "full extent" rather than a stale green.
        const viewData = viewOnly && pinned.chart.recompute ? chartViewData[pinned.id] : undefined;
        const chartToRender = viewData ? { ...pinned.chart, data: viewData } : pinned.chart;
        return (
          <Box key={pinned.id} sx={styles.chartCard}>
            <Box sx={styles.cardFooter}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Typography sx={styles.chartTitle} noWrap>{pinned.chart.title}</Typography>
                {viewOnly && viewData && (
                  <MuiTooltip title="This chart is re-tallied to the features currently in view.">
                    <Typography sx={{
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                      color: '#2f9e5f', border: '1px solid rgba(47,158,95,0.5)', borderRadius: '3px',
                      px: 0.5, py: 0.125, flexShrink: 0, whiteSpace: 'nowrap',
                    }}
                    >
                      Current view
                    </Typography>
                  </MuiTooltip>
                )}
                {viewOnly && !viewData && (
                  <MuiTooltip title="This chart reflects the full geodatabase, not the current view — only single-layer count and sum/avg/min/max charts rescope to the map extent.">
                    <Typography sx={{
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                      color: '#FF9424', border: '1px solid rgba(255,148,36,0.5)', borderRadius: '3px',
                      px: 0.5, py: 0.125, flexShrink: 0, whiteSpace: 'nowrap',
                    }}
                    >
                      Full extent
                    </Typography>
                  </MuiTooltip>
                )}
              </Box>
              <Box sx={styles.cardTools}>
                <MuiTooltip title={pinned.sharedGdbId === loadedGdbId ? 'Shared with your org — click to unshare' : 'Share with your org'}>
                  <span>
                    <IconButton
                      size="small"
                      sx={styles.cardToolBtn}
                      disabled={!loadedGdbId}
                      onClick={() => handleToggleShare(pinned)}
                      aria-label={pinned.sharedGdbId === loadedGdbId ? 'Unshare from org' : 'Share to org'}
                    >
                      <ShareOutlinedIcon sx={{ fontSize: 14, color: pinned.sharedGdbId === loadedGdbId ? 'primary.main' : undefined }} />
                    </IconButton>
                  </span>
                </MuiTooltip>
                <IconButton size="small" sx={styles.unpin} onClick={() => handleUnpinChart(pinned)} aria-label={`Unpin ${pinned.chart.title}`}>
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            </Box>
            <ArtifactErrorBoundary label="pinned-chart">
              <ChatChart chart={chartToRender} onHighlight={handleChartHighlight} onHoverHighlight={handleChartHover} onClearHighlight={handleChartHoverClear} />
            </ArtifactErrorBoundary>
          </Box>
        );
      })}

      {/* Analysis layers (add_spatial_layer): the geometry lives in the 3D viewer; here they get one
          home alongside the other analysis, each with a show/hide toggle + remove. */}
      {viewerLayers.length > 0 && (
        <Typography sx={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: 'text.secondary', mt: 0.5, px: 0.5,
        }}
        >
          Analysis Layers
        </Typography>
      )}
      {viewerLayers.map((layer) => (
        <Box key={layer.id} sx={styles.chartCard}>
          <Box sx={styles.cardFooter}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Box sx={{
                width: 10, height: 10, borderRadius: '2px', flexShrink: 0,
                bgcolor: layer.color || '#01A1DD',
              }}
              />
              <Typography sx={styles.chartTitle} noWrap>{layer.title}</Typography>
            </Box>
            <Box sx={styles.cardTools}>
              {!!layer.key && (
                <MuiTooltip title={layer.sharedGdbId === loadedGdbId ? 'Unshare with org' : 'Share with org'}>
                  <IconButton
                    size="small"
                    sx={styles.cardToolBtn}
                    onClick={() => handleToggleLayerShare(layer)}
                    aria-label={layer.sharedGdbId === loadedGdbId ? `Unshare ${layer.title}` : `Share ${layer.title} with org`}
                  >
                    <ShareOutlinedIcon sx={{ fontSize: 14, color: layer.sharedGdbId === loadedGdbId ? 'primary.main' : undefined }} />
                  </IconButton>
                </MuiTooltip>
              )}
              <MuiTooltip title={layer.visible ? 'Hide on map' : 'Show on map'}>
                <IconButton
                  size="small"
                  sx={styles.cardToolBtn}
                  onClick={() => dispatch(setLayerVisible({ id: layer.id, visible: !layer.visible }))}
                  aria-label={layer.visible ? `Hide ${layer.title}` : `Show ${layer.title}`}
                >
                  {layer.visible
                    ? <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
                    : <VisibilityOffOutlinedIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </MuiTooltip>
              <IconButton size="small" sx={styles.unpin} onClick={() => handleRemoveLayer(layer)} aria-label={`Remove ${layer.title}`}>
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      ))}
      {/* Popup analyses (add_feature_analysis) the user pinned: each is shown in its features' popups
          as a "Khora Analysis" section; remove here to drop it from both the dashboard and the popups. */}
      {popupAnalyses.length > 0 && (
        <Typography sx={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: 'text.secondary', mt: 0.5, px: 0.5,
        }}
        >
          Popup Analyses
        </Typography>
      )}
      {popupAnalyses.map(([layerName, analysis]) => (
        <Box key={`fa-${layerName}`} sx={styles.chartCard}>
          <Box sx={styles.cardFooter}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Box sx={{
                width: 10, height: 10, borderRadius: '50%', flexShrink: 0, bgcolor: '#01A1DD',
              }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={styles.chartTitle} noWrap>{analysis.title || `${layerName} analysis`}</Typography>
                <Typography sx={{ fontSize: 10, color: 'grey.400' }} noWrap>
                  {`${layerName} · ${Object.keys(analysis.byKey || {}).length} features · shown in each popup`}
                </Typography>
              </Box>
            </Box>
            <MuiTooltip title="Remove from dashboard + popups">
              <IconButton size="small" sx={styles.unpin} onClick={() => dispatch(removeFeatureAnalysis(layerName))} aria-label={`Remove ${analysis.title || layerName} popup analysis`}>
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </MuiTooltip>
          </Box>
        </Box>
      ))}
      </Box>
    </Box>
  );
};

export default Dashboard;
